import React from "react"
import { Calendar, Mail, MapPin, Phone, Plus, User } from "lucide-react"
import Badge from "../common/Badge.jsx"
import Button from "../common/Button.jsx"
import useInfiniteScrollTrigger from "../hooks/useInfiniteScrollTrigger.js"

function getStatusBadgeVariant(status) {
    return status === "active" ? "success" : "error"
}

const TRUNCATE_LINE_STYLE = {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
}

export default function CustomersMobileList({
    customers,
    hasMore = false,
    isLoading = false,
    isLoadingInitial = false,
    onLoadMore = () => { },
    onCustomerClick = (_customer) => { },
    onAddOrderClick = (_customer) => { },
    enableStatusColumn = true,
    enableTypeColumn = true,
    selectedCustomerIds = undefined,
    onSelectedCustomerIdsChange = undefined,
}) {
    const sentinelRef = useInfiniteScrollTrigger({
        enabled: true,
        hasMore,
        isLoading,
        onLoadMore,
    })

    function formatLatestOrderDateLabel(value) {
        const parsed = new Date(value)
        if (!Number.isFinite(parsed.getTime())) {
            return String(value)
        }
        return parsed.toISOString().slice(0, 10)
    }

    if (isLoadingInitial && customers.length === 0) {
        return (
            <div className="d-lg-none v2-mobile-list">
                <div className="v2-mobile-list-row bg-white rounded p-3 mb-3 text-secondary w-100">
                    Loading customers...
                </div>
            </div>
        )
    }

    return (
        <div className="d-lg-none v2-mobile-list">
            {customers.map((customer) => {
                const initials = `${customer.first_name?.[0] ?? ""}${customer.last_name?.[0] ?? ""
                    }`.toUpperCase()
                const customerId = customer?.id
                const selectedIds = Array.isArray(selectedCustomerIds) ? selectedCustomerIds : []
                const isSelectable = typeof onSelectedCustomerIdsChange === "function" && customerId != null
                const isChecked = isSelectable && selectedIds.some((id) => String(id) === String(customerId))

                return (
                    <div
                        key={customer.id}
                        className="v2-mobile-list-row bg-white p-3 border-bottom position-relative w-100"
                        style={{
                            cursor: "pointer",
                            backgroundColor: isChecked ? "rgba(15, 97, 112, 0.08)" : undefined,
                        }}
                        onClick={() => onCustomerClick(customer)}
                    >
                        <div className="d-flex justify-content-between align-items-start gap-3 w-100">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div
                                        className="rounded-circle d-flex align-items-center
                                        justify-content-center flex-shrink-0"
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                            backgroundColor: "#0f6170",
                                        }}
                                    >
                                        <span className="text-white fw-semibold"
                                            style={{ fontSize: "0.875rem" }}>
                                            {initials || "?"}
                                        </span>
                                    </div>
                                </div>
                                <div className="ms-3" style={{ minWidth: 0 }}>
                                    <h3
                                        className="fw-semibold text-dark mb-0"
                                        style={{ fontSize: "0.875rem", ...TRUNCATE_LINE_STYLE }}
                                        title={`${customer.first_name ?? "—"} ${customer.last_name ?? "—"}`}
                                    >
                                        {customer.first_name ?? "—"} {customer.last_name ?? "—"}
                                    </h3>
                                    <div
                                        className="text-secondary"
                                        style={{ fontSize: "0.75rem", minWidth: 0 }}
                                    >
                                        <span
                                            style={TRUNCATE_LINE_STYLE}
                                            title={`ID: ${customer.id ?? "—"}`}
                                        >
                                            ID: {customer.id ?? "—"}
                                        </span>
                                    </div>
                                    {customer.company ? (
                                        <div
                                            className="text-secondary"
                                            style={{ fontSize: "0.75rem", minWidth: 0 }}
                                        >
                                            <span
                                                style={TRUNCATE_LINE_STYLE}
                                                title={String(customer.company ?? "")}
                                            >
                                                {customer.company}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div
                                className="flex-shrink-0"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(0, 1fr) auto",
                                    columnGap: "0.5rem",
                                    alignItems: "start",
                                    justifyItems: "end",
                                    maxWidth: "220px",
                                }}
                            >
                                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end" style={{ minWidth: 0 }}>
                                {enableStatusColumn ? (
                                    <Badge
                                        variant={getStatusBadgeVariant(customer.status)}
                                        className="v2-not-implemented-btn"
                                        title="Showing mock data"
                                    >
                                        {customer.status}
                                    </Badge>
                                ) : null}
                                {customer.is_staff ? (
                                    <Badge variant="info">Staff</Badge>
                                ) : null}
                                {enableTypeColumn ? (
                                    (customer.customer_types ?? []).map((type) => (
                                        <Badge
                                            key={type}
                                            variant="info"
                                            className="small v2-not-implemented-btn"
                                            title="Showing mock data"
                                        >
                                            {type}
                                        </Badge>
                                    ))
                                ) : null}
                                </div>
                                <div onClick={(event) => event.stopPropagation()}>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => onAddOrderClick(customer)}
                                    >
                                        <span className="me-1"><Plus size={14} /></span>
                                        Order
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center text-secondary mt-2"
                            style={{ fontSize: "0.875rem" }}>
                            <Mail size={14} className="me-1" />
                            {customer.email ?? "—"}
                        </div>
                        <div className="d-flex align-items-center text-secondary mt-1"
                            style={{ fontSize: "0.875rem" }}>
                            <Phone size={14} className="me-1" />
                            {customer.phone ?? "—"}
                        </div>

                        <div
                            className="d-flex align-items-center gap-3 mt-2"
                            style={{ fontSize: "0.875rem" }}
                        >
                            <div className="d-flex align-items-center text-secondary">
                                <User size={14} className="me-1" />
                                {customer.total_orders} orders
                            </div>
                            {Number(customer.total_orders ?? 0) > 0 &&
                            customer.latest_order_date ? (
                                <div className="d-flex align-items-center text-secondary">
                                    <Calendar size={14} className="me-1" />
                                    {formatLatestOrderDateLabel(customer.latest_order_date)}
                                </div>
                            ) : null}
                        </div>

                        <div className="d-flex align-items-center text-secondary mt-2"
                            style={{ fontSize: "0.875rem" }}>
                            <MapPin size={14} className="me-1" />
                            {customer.ship_to?.city ?? "—"}, {customer.ship_to?.state ?? "—"}
                        </div>

                        {isSelectable ? (
                            <div
                                className="position-absolute bottom-0 end-0 p-2"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <label
                                    className="d-flex align-items-center justify-content-center rounded-circle border shadow-sm"
                                    style={{
                                        width: "44px",
                                        height: "44px",
                                        background: "rgba(255,255,255,0.92)",
                                        cursor: "pointer",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        className="form-check-input m-0"
                                        style={{ width: "1.25rem", height: "1.25rem" }}
                                        checked={Boolean(isChecked)}
                                        onChange={(event) => {
                                            const nextChecked = event.target.checked
                                            const next = new Set(selectedIds.map((x) => String(x)))
                                            const key = String(customerId)
                                            if (nextChecked) next.add(key)
                                            else next.delete(key)
                                            onSelectedCustomerIdsChange(Array.from(next))
                                        }}
                                        aria-label={`Select customer ${customerId ?? ""}`}
                                    />
                                </label>
                            </div>
                        ) : null}
                    </div>
                )
            })}
            <div ref={sentinelRef} style={{ height: "1px" }} />
            {isLoading ? (
                <div className="text-center text-secondary py-3" style={{ fontSize: "0.875rem" }}>
                    Loading more...
                </div>
            ) : null}
            {!hasMore && customers.length > 0 ? (
                <div className="text-center text-secondary py-3" style={{ fontSize: "0.875rem" }}>
                    End of customers
                </div>
            ) : null}
        </div>
    )
}
